import { KycDocumentDiscrepancyDto, KycSmartDocumentDto } from '../../../../../services/kycDocuments.service';

export const DOC_TYPE_LABELS: Record<string, string> = {
  PAN_CARD: 'PAN card',
  PASSPORT: 'passport',
  AADHAR_CARD: 'Aadhaar',
  OCI_CARD: 'OCI card',
  ADDRESS_PROOF: 'address proof',
  CANCELLED_CHEQUE: 'cancelled cheque',
};

/**
 * Human-readable message for a single validation discrepancy. The document-type
 * and required-ID-field rules get a plain-language sentence; everything else
 * falls back to the generic "field: expected X, got Y" form.
 */
export function discrepancyMessage(
  d: KycDocumentDiscrepancyDto,
  documentType: string,
  /** Name the government source confirmed for this document (VERIFIED outcome), if any. */
  verifiedName?: string | null,
): string {
  const label = DOC_TYPE_LABELS[documentType] || documentType;
  if (d.canonicalSource === 'OCR_DOC_TYPE') {
    return `This doesn't look like a ${label}. Please upload the correct document.`;
  }
  if (d.canonicalSource === 'OCR_ID_FIELD') {
    return `We couldn't read ${d.expectedValue || 'a valid ID number'} on this document. Please upload a clear ${label}.`;
  }
  if (d.canonicalSource === 'OCR_QUALITY') {
    return `This ${label} appears blurred or unclear. Please upload a clear photo or scan.`;
  }
  if (d.canonicalSource === 'OCR_INFRA') {
    if (d.severity === 'INFO') {
      return 'Read by the standard document reader (AuthBridge was not used).';
    }
    if (/consent/i.test(d.observedValue || '')) {
      return 'This document was not read automatically because document processing consent was not given. '
        + 'Enable it from My Documents and re-upload, or continue and upload it on the KYC documents page for manual review.';
    }
    if (/not readable|incorrect document|unreadable/i.test(d.observedValue || '')) {
      return `The reader could not make out this ${label}. Please upload a clear photo or scan of the correct document.`;
    }
    return 'Document reading is temporarily unavailable. Please try again shortly.';
  }
  // Plan P9: AuthBridge government-source verification (PAN / passport).
  if (d.canonicalSource === 'GOVT_SOURCE') {
    const record = documentType === 'PASSPORT' ? 'Passport Seva record' : 'Income Tax record';
    if (['first_name', 'middle_name', 'last_name', 'name'].includes(d.fieldName || '')) {
      return `The ${record} shows the name "${d.expectedValue || ''}" for this document. `
        + 'Use "Correct details" if the extracted name was misread.';
    }
    if (d.fieldName === 'verification' && /consent/i.test(d.observedValue || '')) {
      return 'Government verification was not run because document verification is turned off. '
        + 'You can enable it from My Documents.';
    }
    if (d.fieldName === 'pan_status') {
      return `This PAN is not active at the Income Tax Department (${d.observedValue || 'not found'}). `
        + 'Please upload a valid PAN card.';
    }
    if (d.fieldName === 'passport_status') {
      return `This passport could not be confirmed with Passport Seva (${d.observedValue || 'not found'}).`;
    }
    return 'Government verification could not be completed right now; your document is still accepted.';
  }
  // Plan P10: cheque reading vs the bank details already entered.
  if (d.canonicalSource === 'CROSS_DOC' && (d.fieldName === 'account_number' || d.fieldName === 'ifsc_code')) {
    const what = d.fieldName === 'account_number' ? 'account number' : 'IFSC code';
    return `Your bank details already show the ${what} "${d.expectedValue || ''}" but the cheque reads "${d.observedValue || '(blank)'}". `
      + 'Correct the cheque reading or update your Bank Details.';
  }
  // Name-vs-profile mismatch (user 2026-09-04): say plainly that the document does not
  // appear to belong to this applicant, instead of the raw expected/got dump.
  if (d.canonicalSource === 'PROFILE'
      && ['first_name', 'middle_name', 'last_name'].includes(d.fieldName || '')) {
    if (verifiedName) {
      // The source confirmed the name on the card — a misread is ruled out, so no "Correct details" hint.
      const source = documentType === 'PASSPORT' ? 'Passport Seva' : 'the Income Tax Department';
      return `The name on this ${label} is confirmed by ${source} as "${verifiedName}" and does not match your details. `
        + 'Please upload your own document.';
    }
    return `The name on this ${label} does not match your details. Please upload your own document, `
      + `or use "Correct details" if the extracted name was misread.`;
  }
  return `${d.fieldName || d.canonicalSource}: expected "${d.expectedValue}", got "${d.observedValue}"`;
}

/**
 * Joined plain-language text for every BLOCKING reason on a rejected upload,
 * suitable for the shared AlertDialog popup. Returns null when there is nothing
 * blocking to show.
 */
export function blockingAlertText(doc: KycSmartDocumentDto): string | null {
  const blocking = doc.discrepancies.filter((d) => d.severity === 'BLOCKING');
  if (blocking.length === 0) return null;
  return blocking.map((d) => discrepancyMessage(d, doc.documentType)).join(' ');
}
